export const PRINT_ROOT_ID = 'site-map-print-root';

const CSS_DPI = 96;
const PAGE_MARGIN_IN = 0.25;

/** Printable area for US Letter portrait (width × height in CSS px). */
export const getPrintablePageSizePx = () => {
  const pageWidthIn = 8.5 - PAGE_MARGIN_IN * 2;
  const pageHeightIn = 11 - PAGE_MARGIN_IN * 2;

  return {
    width: pageWidthIn * CSS_DPI,
    height: pageHeightIn * CSS_DPI
  };
};

/** Estimated height reserved for header, legend, footer, and gaps in print layout. */
export const PRINT_CHROME_HEIGHT_PX = 120;

/** Map area sized to fill the printable page width and remaining height. */
export const getPrintMapSizePx = () => {
  const page = getPrintablePageSizePx();

  return {
    width: page.width,
    height: Math.max(240, page.height - PRINT_CHROME_HEIGHT_PX)
  };
};

export const getPrintLayout = () => {
  const page = getPrintablePageSizePx();
  const mapSize = getPrintMapSizePx();

  return {
    page,
    mapSize,
    sheetWidth: page.width
  };
};

/** Measure the portaled print sheet at print width while it is hidden off-screen. */
export const measurePrintSheet = () => {
  const root = document.getElementById(PRINT_ROOT_ID);
  const sheet = root?.querySelector('.site-map-print-sheet');
  if (!root || !sheet) return null;

  const previousStyle = root.getAttribute('style') || '';
  root.style.cssText = [
    'display: block',
    'position: absolute',
    'left: -10000px',
    'top: 0',
    'visibility: hidden',
    'width: 8in',
    'box-sizing: border-box'
  ].join(';');

  const size = {
    width: sheet.scrollWidth,
    height: sheet.scrollHeight
  };

  if (previousStyle) {
    root.setAttribute('style', previousStyle);
  } else {
    root.removeAttribute('style');
  }

  return size;
};

export const computePrintScale = sheetSize => {
  if (!sheetSize?.width || !sheetSize?.height) return 1;

  const page = getPrintablePageSizePx();
  return Math.min(page.width / sheetSize.width, page.height / sheetSize.height, 1);
};

export const applyPrintScaleVars = (scale, sheetSize) => {
  const root = document.getElementById(PRINT_ROOT_ID);
  if (!root || !sheetSize) return;

  root.style.setProperty('--print-scale', String(scale));
  root.style.setProperty('--print-content-width', `${sheetSize.width}px`);
  root.style.setProperty('--print-content-height', `${sheetSize.height}px`);
};

export const clearPrintScaleVars = () => {
  const root = document.getElementById(PRINT_ROOT_ID);
  if (!root) return;

  root.style.removeProperty('--print-scale');
  root.style.removeProperty('--print-content-width');
  root.style.removeProperty('--print-content-height');
};

const waitForNextPaint = () =>
  new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

export const triggerBrowserPrint = async () => {
  await waitForNextPaint();

  const sheetSize = measurePrintSheet();
  const scale = computePrintScale(sheetSize);
  applyPrintScaleVars(scale, sheetSize);

  const cleanup = () => {
    document.body.classList.remove('site-map-printing');
    clearPrintScaleVars();
    window.removeEventListener('afterprint', cleanup);
  };

  document.body.classList.add('site-map-printing');
  window.addEventListener('afterprint', cleanup);
  window.print();
};
